/**
 * Chat History Component
 * Displays conversation history with ability to resume or delete conversations
 */

import React from 'react';
import { History, Trash2, Clock, Plus } from 'lucide-react';
import { ScrollArea } from '../ui/scroll-area';
import { sectionNames } from './chatbotConfig';

const ChatHistory = ({
  language,
  conversationHistory,
  onLoadHistory,
  onDeleteHistory,
  onClearAllHistory,
  onNewSession
}) => {
  // Format date for display
  const formatHistoryDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return language === 'pt' ? 'Hoje' : 'Today';
    } else if (diffDays === 1) {
      return language === 'pt' ? 'Ontem' : 'Yesterday';
    } else if (diffDays < 7) {
      return language === 'pt' ? `${diffDays} dias atrás` : `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString(language === 'pt' ? 'pt-PT' : 'en-US', { 
        day: 'numeric', 
        month: 'short' 
      });
    }
  };

  // Format time for display
  const formatHistoryTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString(language === 'pt' ? 'pt-PT' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <ScrollArea className="flex-1 p-4">
      {conversationHistory.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-center py-12">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
            <History className="w-8 h-8 text-slate-400" />
          </div>
          <h4 className="text-slate-600 font-medium mb-2">
            {language === 'pt' ? 'Sem histórico' : 'No history'}
          </h4>
          <p className="text-sm text-slate-400 max-w-[250px]">
            {language === 'pt' 
              ? 'As suas conversas anteriores aparecerão aqui.'
              : 'Your previous conversations will appear here.'}
          </p>
        </div>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-medium text-slate-400 uppercase tracking-wider">
              {language === 'pt' ? `${conversationHistory.length} conversa(s)` : `${conversationHistory.length} conversation(s)`}
            </p>
            <button
              onClick={onClearAllHistory}
              className="text-xs text-red-500 hover:text-red-600 flex items-center gap-1 transition-colors"
              data-testid="clear-history-btn"
            >
              <Trash2 className="w-3 h-3" />
              {language === 'pt' ? 'Limpar tudo' : 'Clear all'}
            </button>
          </div>
          <div className="flex flex-col gap-2">
            {conversationHistory.map((item) => (
              <div
                key={item.id}
                onClick={() => onLoadHistory(item)}
                className="group bg-slate-50 hover:bg-slate-100 rounded-lg p-3 cursor-pointer transition-colors border border-slate-200 hover:border-slate-300"
                data-testid={`history-item-${item.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full font-medium">
                        {sectionNames[item.topic]?.[language] || (language === 'pt' ? 'Geral' : 'General')}
                      </span>
                      <span className="text-xs text-slate-400 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatHistoryTime(item.timestamp)}
                      </span>
                    </div>
                    <p className="text-sm text-slate-700 truncate font-medium">
                      {item.preview}...
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      {formatHistoryDate(item.timestamp)} • {item.messageCount} {language === 'pt' ? 'mensagens' : 'messages'}
                    </p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteHistory(item.id);
                    }}
                    className="opacity-0 group-hover:opacity-100 p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-all"
                    data-testid={`delete-history-${item.id}`}
                    title={language === 'pt' ? 'Eliminar' : 'Delete'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* New conversation button */}
      <button
        onClick={onNewSession}
        className="w-full mt-4 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center justify-center gap-2"
        data-testid="new-conversation-btn"
      >
        <Plus className="w-4 h-4" />
        {language === 'pt' ? 'Nova Conversa' : 'New Conversation'}
      </button>
    </ScrollArea>
  );
};

export default ChatHistory;
